/**
 * Detail drawer for one DFY Producer Vault pack.
 *
 * Transcribed from the monolith's `vaultOpenPack && (...)` block. Seven resource
 * tabs: Overview, Prompts, Genre Presets, Structures, Hooks, Metadata, Marketing.
 *
 * Unlike the Studio drawer this one is NOT a route. The Studio needed a URL
 * because a song is a shareable object with its own deep link; a vault pack is a
 * browsing detail view over static fixtures, and §1 does not list a path for it.
 * Its open/closed state stays in vaultStore, which also means the drawer survives
 * a tab switch inside the Vault the same way it did before.
 *
 * SIMULATED. "Download ZIP" writes a text manifest client-side — see
 * lib/download.ts. The button keeps its original label because the design says
 * ZIP; the file it produces does not match that label yet, which is a Phase 3+
 * item, not a copy change to make here.
 */
import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { ArrowRight, Check, Copy, Download, Package, Sliders, Star, X } from 'lucide-react';
import { Btn, IconButton, PreviewChip, TabBar } from '../ui';
import type { Tab } from '../ui';
import { ArtworkPlaceholder } from '../songs';
import { focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import { c } from '../../styles/theme';
import type { VaultResourceTab } from '../../stores/vaultStore';
import type { VaultPack, VaultPreset, VaultPrompt } from '../../types/domain';

export interface VaultPackDrawerProps {
  pack: VaultPack;
  resourceTab: VaultResourceTab;
  onSelectResourceTab: (tab: VaultResourceTab) => void;
  onClose: () => void;
  favorited: boolean;
  onToggleFavorite: () => void;
  /** Item ids favourited across the whole vault. */
  favoriteItems: string[];
  onToggleItemFavorite: (itemId: string) => void;
  onDownload: () => void;
  onCopy: (text: string) => void;
  onUsePrompt: (prompt: VaultPrompt) => void;
  onLoadPreset: (preset: VaultPreset) => void;
}

/** Card wrapper shared by every resource row. Styles verbatim. */
function ResourceCard({
  children,
  row,
}: {
  children: ReactNode;
  /** Lays the card out as a single row with the actions pushed right. */
  row?: boolean;
}) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        background: c.surface2,
        ...(row
          ? {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }
          : null),
      }}
    >
      {children}
    </div>
  );
}

/**
 * Vertical stack of resource cards, with the tab's empty-state line.
 *
 * `count` is passed rather than inferred from `children`: React.Children counting
 * would see one mapped array and report 1, which is exactly the kind of quiet
 * wrongness that makes an empty state never appear.
 */
function ResourceList({
  empty,
  count,
  children,
}: {
  empty: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {count === 0 && <p style={{ fontSize: 12, color: c.text3 }}>{empty}</p>}
      {children}
    </div>
  );
}

/** One of the six counters on the Overview tab. */
function CountCell({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        padding: '8px 10px',
        background: c.surface2,
      }}
    >
      <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>{value}</p>
      <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>{label}</p>
    </div>
  );
}

const BODY_TEXT = { fontSize: 12, color: c.text2, margin: '0 0 10px', lineHeight: 1.6 } as const;
const ROW_TITLE = { fontSize: 13, fontWeight: 500, margin: 0 } as const;

export default function VaultPackDrawer({
  pack,
  resourceTab,
  onSelectResourceTab,
  onClose,
  favorited,
  onToggleFavorite,
  favoriteItems,
  onToggleItemFavorite,
  onDownload,
  onCopy,
  onUsePrompt,
  onLoadPreset,
}: VaultPackDrawerProps) {
  /*
    ============================================================================
    THE MODAL MECHANICS THIS SURFACE HAD NONE OF.

    It is a `.lyx-drawer`: `position: fixed` at `--lyx-z-drawer`, full height, 420px wide,
    behind a full-viewport `rgba(0,0,0,0.55)` scrim — and below 560px a 92dvh bottom sheet,
    i.e. the whole phone. So it is a modal surface by every behaviour except the ones that
    make one usable, and it had zero of them: no `role="dialog"`, no `aria-modal`, no focus
    move-in, no focus restore, no Tab containment, no Escape, no background scroll lock.
    Opening a pack left the keyboard on the "Open <pack>" button behind the scrim, Tab then
    walked an invisible page under a panel covering it, the page behind still scrolled, and
    a screen reader was never told anything had opened.

    NOTHING VISUAL CHANGES, and the markup is untouched apart from the attributes. This is
    the same three-effects-plus-attributes set `ui/Modal.tsx` applies to every dialog and
    `layout/Sidebar.tsx` hand-rolls for the nav drawer — the two places the pattern is
    already written down. It is hand-rolled here for the reason Sidebar's is: Modal portals
    to `document.body` and owns its own overlay and box geometry, so routing this through it
    would replace the drawer's layout rather than add semantics to it.

    NO `open` PROP, unlike Modal's: routes/Vault.tsx mounts this only when a pack is open, so
    mounted IS open and the effects need no guard. That is also why the cleanups are the
    close path — unmount is how this drawer closes.
    ============================================================================
  */
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();

  /*
    Focus in, and back out on close.

    The first focusable IS the Close button — the heading beside it is a `<p>` — which is
    Sidebar's chosen target and for its reason: it is the way out, and someone who opened
    this by accident should find the exit first. Queried rather than held on a ref because
    `IconButton` does not forward one, and adding ref forwarding to a primitive used in
    twenty-odd places to reach one button here would be the larger change.
  */
  useEffect(() => {
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // `focusableWithin`'s first entry rather than a `button` query, so "the first focusable"
    // means the same thing here as it does in the Tab wrap below — a drawer whose first
    // control was ever a link or a field would otherwise start focus in a different place
    // from where Shift+Tab wraps to.
    const root = rootRef.current;
    if (root) focusableWithin(root)[0]?.focus();
    return () => {
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      // Only if it is still on the page: the Use-prompt and Load-preset actions navigate to
      // /create, and dragging focus back to a card on a screen the user has left would take
      // it off the one they asked for.
      if (target?.isConnected) target.focus();
    };
  }, []);

  // The page behind does not scroll while a panel covers it — the same lock Modal and the
  // full-screen player apply, and the reason this one was noticeable is that the drawer has
  // its own `overflow-y: auto`, so a wheel gesture past its end scrolled the Vault instead.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Escape closes, and Tab stays inside. Capture phase and `stopPropagation` on Escape so
  // the Studio's own Escape shortcut cannot also fire — FullscreenPlayer's note, same
  // reason. The wrap comes from lib/focusTrap.ts, shared with Modal, the nav drawer and the
  // full-screen player: there is no platform primitive for it short of `inert` on everything
  // else, so the one implementation is the closest thing to one.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      wrapTabFocus(root, e);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const tabs: Tab<VaultResourceTab>[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'prompts', label: `Prompts (${pack.prompts.length})` },
    { key: 'presets', label: `Genre Presets (${pack.presets.length})` },
    { key: 'structures', label: `Structures (${pack.structures.length})` },
    { key: 'hooks', label: `Hooks (${pack.hooks.length})` },
    { key: 'metadata', label: `Metadata (${pack.metadata.length})` },
    { key: 'marketing', label: `Marketing (${pack.marketing.length})` },
  ];

  /** Star toggle for one item inside the pack. */
  const itemStar = (id: string, size = 13) => {
    const fav = favoriteItems.includes(id);
    return (
      <IconButton
        label={fav ? 'Remove from favorites' : 'Add to favorites'}
        onClick={() => onToggleItemFavorite(id)}
      >
        <Star size={size} color={fav ? c.accent : c.text3} fill={fav ? c.accent : 'none'} />
      </IconButton>
    );
  };

  return (
    <>
      {/* Decorative: the dismissal it offers is the Close button's, which is a real control
          inside the dialog. Without this a screen reader announces an unlabelled clickable
          region covering the page. */}
      <div className="lyx-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        // The visible "Pack Details" heading names it, plus the pack — a user with seven of
        // these in their history needs to know WHICH pack opened.
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{ background: c.surface1, borderLeft: `1px solid ${c.border}` }}
      >
        <div style={{ padding: '18px 20px 32px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <p
              id={headingId}
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 15,
                margin: 0,
              }}
            >
              {/* The pack's name is inside the heading element, not only in the body, so
                  `aria-labelledby` names the dialog rather than announcing seven
                  identical "Pack Details" dialogs. Visually hidden because the pack title
                  is already rendered prominently below. */}
              Pack Details
              <span className="lyx-visually-hidden"> — {pack.name}</span>
            </p>
            <IconButton label="Close" onClick={onClose}>
              <X size={18} color={c.text3} />
            </IconButton>
          </div>

          <ArtworkPlaceholder
            id={pack.id}
            height={110}
            depth="hero"
            saturation={[50, 45]}
            hueMultiplier={3}
            style={{ borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}
          >
            <Package
              size={26}
              color="rgba(255,255,255,0.5)"
              style={{ position: 'absolute', bottom: 12, left: 14 }}
            />
            <span
              style={{
                position: 'absolute',
                top: 10,
                left: 14,
                fontSize: 10,
                color: '#fff',
                background: 'rgba(0,0,0,0.4)',
                padding: '2px 8px',
                borderRadius: 999,
              }}
            >
              {pack.category}
            </span>
            <IconButton
              label={favorited ? 'Remove pack from favorites' : 'Add pack to favorites'}
              onClick={onToggleFavorite}
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                background: 'rgba(0,0,0,0.4)',
                borderRadius: '50%',
                width: 28,
                height: 28,
              }}
            >
              <Star
                size={14}
                color={favorited ? c.accent : '#fff'}
                fill={favorited ? c.accent : 'none'}
              />
            </IconButton>
          </ArtworkPlaceholder>

          <p
            style={{
              fontWeight: 600,
              fontSize: 16,
              margin: '0 0 4px',
              fontFamily: 'Space Grotesk',
            }}
          >
            {pack.name}
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 10px', lineHeight: 1.6 }}>
            {pack.description}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 11, color: c.text3 }}>By {pack.author}</span>
            <span style={{ fontSize: 11, color: c.text3 }}>v{pack.version}</span>
            <span style={{ fontSize: 11, color: c.text3 }}>Released {pack.releaseDate}</span>
            <span style={{ fontSize: 11, color: c.text3 }}>Updated {pack.updated}</span>
          </div>
          <Btn full onClick={onDownload} style={{ marginBottom: 16 }}>
            <Download size={13} />
            Download ZIP
          </Btn>

          <TabBar
            tabs={tabs}
            active={resourceTab}
            onSelect={onSelectResourceTab}
            label={`${pack.name} resources`}
            scrollable
            compact
            marginBottom={14}
          />

          {resourceTab === 'overview' && (
            <div className="lyx-grid-2">
              <CountCell label="Prompts" value={pack.prompts.length} />
              <CountCell label="Presets" value={pack.presets.length} />
              <CountCell label="Structures" value={pack.structures.length} />
              <CountCell label="Hooks" value={pack.hooks.length} />
              <CountCell label="Metadata" value={pack.metadata.length} />
              <CountCell label="Marketing" value={pack.marketing.length} />
            </div>
          )}

          {resourceTab === 'prompts' && (
            <ResourceList empty="No prompts in this pack." count={pack.prompts.length}>
              {pack.prompts.map((pr) => (
                <ResourceCard key={pr.id}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <p style={ROW_TITLE}>{pr.title}</p>
                    {itemStar(pr.id)}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <PreviewChip>{pr.genre}</PreviewChip>
                    <PreviewChip>{pr.mood}</PreviewChip>
                    <PreviewChip>{pr.difficulty}</PreviewChip>
                  </div>
                  <p style={BODY_TEXT}>{pr.text}</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="secondary" onClick={() => onCopy(pr.text)}>
                      <Copy size={12} />
                      Copy
                    </Btn>
                    <Btn onClick={() => onUsePrompt(pr)}>
                      <ArrowRight size={12} />
                      Use Prompt
                    </Btn>
                  </div>
                </ResourceCard>
              ))}
            </ResourceList>
          )}

          {resourceTab === 'presets' && (
            <ResourceList empty="No genre presets in this pack." count={pack.presets.length}>
              {pack.presets.map((ps) => (
                <ResourceCard key={ps.id}>
                  <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 6px' }}>{ps.name}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    <PreviewChip>Tempo: {ps.tempo}</PreviewChip>
                    <PreviewChip>Mood: {ps.mood}</PreviewChip>
                    <PreviewChip>Energy: {ps.energy}%</PreviewChip>
                    <PreviewChip>{ps.vocals}</PreviewChip>
                  </div>
                  <p style={{ fontSize: 11, color: c.text3, margin: '0 0 10px' }}>
                    Structure: {ps.structure}
                  </p>
                  <Btn onClick={() => onLoadPreset(ps)}>
                    <Sliders size={12} />
                    Load Preset
                  </Btn>
                </ResourceCard>
              ))}
            </ResourceList>
          )}

          {resourceTab === 'structures' && (
            <ResourceList empty="No structures in this pack." count={pack.structures.length}>
              {pack.structures.map((st) => (
                <ResourceCard key={st.id} row>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>{st.name}</p>
                    <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{st.layout}</p>
                  </div>
                  <IconButton
                    label="Copy structure"
                    onClick={() => onCopy(st.layout)}
                    style={{ flexShrink: 0 }}
                  >
                    <Copy size={13} color={c.text3} />
                  </IconButton>
                </ResourceCard>
              ))}
            </ResourceList>
          )}

          {resourceTab === 'hooks' && (
            <ResourceList empty="No hooks in this pack." count={pack.hooks.length}>
              {pack.hooks.map((h) => (
                <ResourceCard key={h.id} row>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 12, margin: '0 0 4px', fontStyle: 'italic' }}>
                      &quot;{h.text}&quot;
                    </p>
                    <PreviewChip>{h.genre}</PreviewChip>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {itemStar(h.id)}
                    <IconButton label="Copy hook" onClick={() => onCopy(h.text)}>
                      <Copy size={13} color={c.text3} />
                    </IconButton>
                  </div>
                </ResourceCard>
              ))}
            </ResourceList>
          )}

          {resourceTab === 'metadata' && (
            <ResourceList empty="No metadata templates in this pack." count={pack.metadata.length}>
              {pack.metadata.map((m) => (
                <ResourceCard key={m.id}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                    }}
                  >
                    <p style={ROW_TITLE}>{m.title}</p>
                    <PreviewChip>{m.platform}</PreviewChip>
                  </div>
                  <p style={BODY_TEXT}>{m.text}</p>
                  {/* Both buttons copy. That is the prototype's behaviour: "Use"
                      has no destination for a metadata template yet, so it does
                      the only useful thing available. Left as-is rather than
                      quietly removed. */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="secondary" onClick={() => onCopy(m.text)}>
                      <Copy size={12} />
                      Copy
                    </Btn>
                    <Btn variant="secondary" onClick={() => onCopy(m.text)}>
                      <Check size={12} />
                      Use
                    </Btn>
                  </div>
                </ResourceCard>
              ))}
            </ResourceList>
          )}

          {resourceTab === 'marketing' && (
            <ResourceList empty="No marketing assets in this pack." count={pack.marketing.length}>
              {pack.marketing.map((m) => (
                <ResourceCard key={m.id}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                    }}
                  >
                    <p style={ROW_TITLE}>{m.title}</p>
                    <PreviewChip>{m.platform}</PreviewChip>
                  </div>
                  <p style={BODY_TEXT}>{m.text}</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="secondary" onClick={() => onCopy(m.text)}>
                      <Copy size={12} />
                      Copy
                    </Btn>
                    {itemStar(m.id, 15)}
                  </div>
                </ResourceCard>
              ))}
            </ResourceList>
          )}
        </div>
      </div>
    </>
  );
}
